/**
 * Workflow Persist Transform
 *
 * This transform ensures only the active workflow encounter ID is persisted to localStorage.
 * The lastAccessedAt timestamp is intentionally NOT persisted since it represents runtime state.
 *
 * @module features/encounters/store/workflowPersistTransform
 */

import { createTransform } from 'redux-persist';

import type { WorkflowState } from '../types';

/**
 * Partial workflow state that gets persisted (only the ID, not the timestamp)
 */
interface PersistedWorkflowState {
  activeWorkflowEncounterId: string | null;
}

/**
 * Transform that persists only the workflow encounter ID
 *
 * This ensures:
 * - User can resume their draft after browser refresh
 * - Timestamps don't accumulate in storage
 * - Storage size stays minimal
 */
export const workflowOnlyIdTransform = createTransform<WorkflowState, PersistedWorkflowState>(
  // INBOUND: State going to storage (persist)
  // Extract only the encounter ID
  (inboundState: WorkflowState) => ({
    activeWorkflowEncounterId: inboundState.activeWorkflowEncounterId,
  }),

  // OUTBOUND: State coming from storage (rehydrate)
  // Restore with null timestamp (will be set when user resumes)
  (outboundState: PersistedWorkflowState | undefined) => {
    if (!outboundState) {
      return {
        activeWorkflowEncounterId: null,
        lastAccessedAt: null,
      };
    }

    return {
      activeWorkflowEncounterId: outboundState.activeWorkflowEncounterId,
      lastAccessedAt: null, // Never restore timestamp from storage
    };
  },

  // Only apply to workflow slice
  { whitelist: ['workflow'] }
);
